import * as XLSX from "xlsx";

export type TableRow = Record<string, string>;

function cleanCell(value: unknown) {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
}

function csvEscape(value: unknown) {
  const text = cleanCell(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function downloadCsv(filename: string, rows: TableRow[]) {
  const headers = Array.from(rows.reduce((set, row) => {
    Object.keys(row).forEach((key) => set.add(key));
    return set;
  }, new Set<string>()));
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => headers.map((header) => csvEscape(row[header])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadMailingLabels(filename: string, rows: TableRow[]) {
  downloadCsv(filename, rows.map((row) => ({
    Name: row.Name || row.displayName || row.familyName || row.parentName || "",
    "Address Line 1": row.address || row.Address || row["Address Line 1"] || "",
    "Address Line 2": row["Address Line 2"] || "",
    City: row.city || row.City || "",
    State: row.state || row.State || "",
    ZIP: row.zip || row.ZIP || row.Zip || "",
  })));
}

export async function parseSpreadsheet(file: File): Promise<{ headers: string[]; rows: TableRow[] }> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { headers: [], rows: [] };
  const worksheet = workbook.Sheets[sheetName];
  const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: "" });
  const headers = Array.from(data.reduce((set, row) => {
    Object.keys(row).forEach((key) => set.add(key));
    return set;
  }, new Set<string>()));
  return {
    headers,
    rows: data.map((row) => Object.fromEntries(headers.map((header) => [header, cleanCell(row[header])]))),
  };
}

export function uniqueEmails(values: string[]) {
  return Array.from(new Set(values.map((value) => value.trim().toLowerCase()).filter((value) => value.includes("@"))));
}
